import React, { useEffect, useMemo, useState } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import * as S from '../Advances/AdvancesStyles';
import { getTeamAttendance, overrideAttendance } from '../../../../services/attendanceService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import AttendanceDrawer from './AttendanceDrawer';

const todayStr = () => new Date().toISOString().split('T')[0];

const fmtTime = t => (t ? new Date(t).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—');

const STATUS_VARIANT = {
  PRESENT: 'approved',
  HALF_DAY: 'pending',
  ON_LEAVE: 'settled',
  HOLIDAY: 'advance',
  ABSENT: 'rejected',
  PENDING_OUT: 'pending',
};

/**
 * The Ledger-equivalent asked for: "everything comes together, one table." Real backend
 * constraint (see attendanceService.js's getTeamAttendance comment): there is no
 * month-range team endpoint yet, only single-date — so this is a date-picker-driven
 * daily log, filterable by project/status/name, not a month-long ledger. Once Devanshu
 * extends getTeamAttendance to accept a real date range, this can switch to a From/To
 * filter with no other changes needed.
 */
const AttendanceLogTab = ({ projects }) => {
  const [date, setDate] = useState(todayStr());
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [fProject, setFProject] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fSearch, setFSearch] = useState('');
  const [drawerUser, setDrawerUser] = useState(null);

  const load = async () => {
    setLoading(true);
    const recs = await getTeamAttendance(date, fProject || undefined);
    setRecords(recs);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, fProject]);

  const filtered = useMemo(() => {
    return records.filter(r => {
      if (fStatus && r.status !== fStatus) return false;
      if (fSearch) {
        const name = (r.user_id?.name || '').toLowerCase();
        if (!name.includes(fSearch.toLowerCase())) return false;
      }
      return true;
    });
  }, [records, fStatus, fSearch]);

  const handleOverride = async (record) => {
    const { value: formValues } = await Swal.fire({
      title: `Override attendance — ${record.user_id?.name || 'staff'}`,
      html:
        '<select id="swal-status" class="swal2-select" style="width:80%">' +
        ['PRESENT', 'HALF_DAY', 'ABSENT', 'ON_LEAVE', 'HOLIDAY']
          .map(s => `<option value="${s}" ${s === record.status ? 'selected' : ''}>${s}</option>`)
          .join('') +
        '</select>' +
        '<input id="swal-reason" class="swal2-input" placeholder="Reason for override (required)">',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Save Override',
      preConfirm: () => {
        const status = document.getElementById('swal-status').value;
        const reason = document.getElementById('swal-reason').value;
        if (!reason) {
          Swal.showValidationMessage('A reason is required — it is kept on the record.');
          return false;
        }
        return { status, reason };
      },
    });

    if (!formValues) return;
    try {
      await overrideAttendance(record._id, { status: formValues.status, override_reason: formValues.reason });
      toast.success('Attendance record updated.');
      load();
    } catch (err) {
      console.error('Override failed:', err);
      toast.error('Failed to update attendance record.');
    }
  };

  const columns = [
    {
      dataField: 'user_id',
      text: 'Staff',
      formatter: (cell) => (
        <span
          className="member-link"
          style={{ cursor: 'pointer' }}
          onClick={() => setDrawerUser({ id: cell?._id, name: cell?.name || 'Staff' })}
        >
          {cell?.name || 'Staff'}
        </span>
      ),
    },
    { dataField: 'date', text: 'Date' },
    {
      dataField: 'status',
      text: 'Status',
      formatter: (cell) => <S.Pill variant={STATUS_VARIANT[cell] || 'default'}>{cell?.replace('_', ' ')}</S.Pill>,
    },
    {
      dataField: 'punch_in',
      text: 'Punch In',
      formatter: (cell) => fmtTime(cell?.time),
    },
    {
      dataField: 'punch_out',
      text: 'Punch Out',
      formatter: (cell) => fmtTime(cell?.time),
    },
    {
      dataField: 'computed_hours',
      text: 'Hours',
      formatter: (cell) => (cell || cell === 0 ? `${cell} hrs` : '—'),
    },
    {
      dataField: 'is_manual_override',
      text: 'Source',
      formatter: (cell) => (cell ? <S.Chip>Manual override</S.Chip> : <span className="rowmuted">App punch</span>),
    },
    {
      dataField: 'action',
      text: '',
      isDummyField: true,
      formatter: (cell, row) => (
        <S.Btn ghost sm onClick={() => handleOverride(row)}>
          Override
        </S.Btn>
      ),
    },
  ];

  return (
    <div>
      <S.FiltersBar>
        <div className="field">
          <label>Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} max={todayStr()} />
        </div>
        <div className="field">
          <label>Project</label>
          <select value={fProject} onChange={e => setFProject(e.target.value)}>
            <option value="">All Projects</option>
            {(projects || []).map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Status</label>
          <select value={fStatus} onChange={e => setFStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="PRESENT">Present</option>
            <option value="HALF_DAY">Half Day</option>
            <option value="ON_LEAVE">On Leave</option>
            <option value="HOLIDAY">Holiday</option>
            <option value="ABSENT">Absent</option>
          </select>
        </div>
        <div className="field grow">
          <label>Search Name</label>
          <input type="text" placeholder="Search staff…" value={fSearch} onChange={e => setFSearch(e.target.value)} />
        </div>
      </S.FiltersBar>

      <S.TableWrap>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading…</div>
        ) : (
          <BootstrapTable
            keyField="_id"
            data={filtered}
            columns={columns}
            bordered={false}
            hover
            noDataIndication={() => (
              <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>
                No attendance records for {date} with the current filters.
              </div>
            )}
            pagination={paginationFactory({ sizePerPage: 50, sizePerPageList: [50, 100, 200, 500], showTotal: true })}
          />
        )}
      </S.TableWrap>

      <AttendanceDrawer
        show={!!drawerUser}
        userId={drawerUser?.id}
        userName={drawerUser?.name}
        month={Number(date.split('-')[1])}
        year={Number(date.split('-')[0])}
        projectId={fProject || undefined}
        editable
        saveLabel="Save Attendance"
        onClose={() => setDrawerUser(null)}
        onSaved={() => {
          setDrawerUser(null);
          load();
        }}
      />
    </div>
  );
};

export default AttendanceLogTab;
